import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, debounceTime, tap } from 'rxjs';
import {
    AangifteSchenkbelasting,
    GiftTaxEntity,
    MenuInterface,
    SbBerekening,
    Vrijstellingen
} from './api.bokkz.services';
import { ErrorMessage } from './rekenclient.api';

@Injectable({ providedIn: 'root' })
export class BokkzSbDataService implements OnDestroy {

    public giftTaxEntity = new BehaviorSubject<GiftTaxEntity>(this.getFromSessionStorage<GiftTaxEntity>('giftTaxEntity') || {} as GiftTaxEntity);
    public sbBerekening = new BehaviorSubject<SbBerekening>(this.getFromSessionStorage<SbBerekening>('sbBerekening') || {} as SbBerekening);
    public menuInterface = new BehaviorSubject<MenuInterface[]>([]);
    public errorResponse = new BehaviorSubject<ErrorMessage[]>([]);
    public aantalValidaties = new BehaviorSubject<number>(0);
    public valide = new BehaviorSubject<boolean>(false);
    public hasContanten = new BehaviorSubject<boolean>(false);
    public hasEffecten = new BehaviorSubject<boolean>(false);
    public hasOnroerendeZaken = new BehaviorSubject<boolean>(false);
    public hasNLOnroerendeZaken = new BehaviorSubject<boolean>(false);
    public hasOnderneming = new BehaviorSubject<boolean>(false);
    public hasPU = new BehaviorSubject<boolean>(false);
    public hasSchuldigerkenning = new BehaviorSubject<boolean>(false);
    public hasKwijtschelding = new BehaviorSubject<boolean>(false);
    public hasRoerendeZaken = new BehaviorSubject<boolean>(false);
    public hasVruchtgebruiken = new BehaviorSubject<boolean>(false);
    public hasOverige = new BehaviorSubject<boolean>(false);
    public soort = new BehaviorSubject<string>('null');
    public year = {} as number;

    constructor() {
        this.applyDebounceTime();
    }

    private applyDebounceTime() {
        const debounceDelay = 500;
        this.giftTaxEntity.pipe(
            debounceTime(debounceDelay),
            tap((value) => this.saveToSessionStorage('giftTaxEntity', value))).subscribe();
        this.sbBerekening = this.sbBerekening.pipe(
            tap((value) => this.saveToSessionStorage('sbBerekening', value))) as BehaviorSubject<SbBerekening>;
        this.errorResponse = this.errorResponse as BehaviorSubject<ErrorMessage[]>;
        this.aantalValidaties = this.aantalValidaties as BehaviorSubject<number>;
        this.valide = this.valide as BehaviorSubject<boolean>;
    }

    private getFromSessionStorage<T>(key: string): T | null {
        const value = sessionStorage.getItem(key);
        return value ? JSON.parse(value) : null;
    }

    private saveToSessionStorage(key: string, value: any) {
        sessionStorage.setItem(key, JSON.stringify(value));
    }

    updateGiftTaxEntity(giftTaxEntity: GiftTaxEntity) {
        this.year = giftTaxEntity.year || 0;
        if (giftTaxEntity.aangifte != null) {
            this.updateMenuInterface(giftTaxEntity.aangifte);
        }
        if (giftTaxEntity?.aangifte?.schenker?.bsn != null) {
            giftTaxEntity.aangifte.schenker.bsn = giftTaxEntity.aangifte.schenker.bsn.padStart(9, '0');
        }
        this.giftTaxEntity.next(giftTaxEntity);
    }

    updateSbBerekening(sbBerekening: SbBerekening) {
        this.sbBerekening.next(sbBerekening);
    }

    updateErrorResponse(errorResponse: ErrorMessage[]) {
        this.errorResponse.next(errorResponse);
    }

    updateAantalValidaties(aantalValidaties: number) {
        this.valide.next(aantalValidaties === 0);
        this.aantalValidaties.next(aantalValidaties);
    }

    updateAangifteSchenkbelasting(sbAangifte: AangifteSchenkbelasting) {

        if (sbAangifte.contactPersoon?.beconnummer != null) {
            if (sbAangifte.contactPersoon?.beconnummer === '') {
                sbAangifte.contactPersoon.beconnummer = undefined;
            }
            if (sbAangifte.contactPersoon?.bsn != null) {
                sbAangifte.contactPersoon.bsn = undefined;
            }
        }

        // 1. Reset alle menu-items
        this.hasContanten.next(false);
        this.hasEffecten.next(false);
        this.hasOnroerendeZaken.next(false);
        this.hasOnderneming.next(false);
        this.hasPU.next(false);
        this.hasSchuldigerkenning.next(false);
        this.hasKwijtschelding.next(false);
        this.hasRoerendeZaken.next(false);
        this.hasVruchtgebruiken.next(false);
        this.hasOverige.next(false);
        this.hasNLOnroerendeZaken.next(false);

        // 2. als soort.value een waarde heeft, is het UI dus bepalen we de interface op basis van de UI.
        const v = this.soort.value;
        if (v && v !== 'null') {
            if (v.includes('CONTANTEN')) this.hasContanten.next(true);
            else if (v.includes('EFFECTEN')) this.hasEffecten.next(true);
            else if (v.includes('VASTGOED')) this.hasOnroerendeZaken.next(true);
            else if (v.includes('ONDERNEMING')) this.hasOnderneming.next(true);
            else if (v.includes('UITKERING')) this.hasPU.next(true);
            else if (v.includes('SCHENKING OP PAPIER')) this.hasSchuldigerkenning.next(true);
            else if (v.includes('KWIJTSCHELDING')) this.hasKwijtschelding.next(true);
            else if (v.includes('ROERENDEZAAK')) this.hasRoerendeZaken.next(true);
            else if (v.includes('VRUCHTGEBRUIK')) this.hasVruchtgebruiken.next(true);
            else if (v.includes('OVERIG')) this.hasOverige.next(true);
        }
        // 3. geen soort dus backend data we gaan nu op basis van de aangifte de menu interface bouwen
        else if (sbAangifte && sbAangifte.schenking && sbAangifte.schenking.length > 0) {
            const schenking = sbAangifte.schenking[0];
            if (schenking.contanten) this.hasContanten.next(true);
            if (schenking.effecten) this.hasEffecten.next(true);
            if (schenking.onroerendeZaken) this.hasOnroerendeZaken.next(true);
            if (schenking.ondernemingen) this.hasOnderneming.next(true);
            if (schenking.periodiekeUitkering) this.hasPU.next(true);
            if (schenking.schuldigerkenning) this.hasSchuldigerkenning.next(true);
            if (schenking.kwijtscheldingSchuld) this.hasKwijtschelding.next(true);
            if (schenking.roerendeZaak) this.hasRoerendeZaken.next(true);
            if (schenking.vruchtgebruik) this.hasVruchtgebruiken.next(true);
            if (schenking.overige) this.hasOverige.next(true);
        }

        // 4. Nu gaan we op basis van de vlaggen de aangifte schoonmaken.
        if (sbAangifte && sbAangifte.schenking && sbAangifte.schenking.length > 0) {
            const schenking = sbAangifte.schenking[0];
            let vrijstellingen = schenking.vrijstellingen;
            if (vrijstellingen === undefined) {
                vrijstellingen = {
                    eenmaligVerhoogdeVrijstelling: false,
                    eenmaligVerhoogdeVrijstellingEigenWoning: false,
                    bedragAlBetaaldEigenWoning: null as unknown as number,
                    bedragNogTeBestedenEigenWoning: null as unknown as number,
                    verhoogdeVrijstellingStudie: false,
                    bedragAlBetaaldDureStudie: null as unknown as number,
                    bedragNogTeBestedenDureStudie: null as unknown as number,
                    vrijstellingSchuldenaar: false,
                    vrijstellingNatuurlijkeVerbintenis: false,
                    vrijstellingAnbiOfSbbi: false,
                    vrijstellingSchenkingDoorKoning: false,
                    vrijstellingSchenkingDoorStaat: false,
                    vrijstellingSchenkingBelastInIB: false,
                    vrijstellingSchenkingAanNabestaandeWerknemer: false,
                    vrijstellingOverig: null as unknown as string,
                    alGebruikteBasisvrijstelling: null as unknown as number,
                    alGebruikteEenmaligeVerhoogdeVrijstelling: null as unknown as number,
                    alGebruikteVrijstellingDureStudie: null as unknown as number,
                    vrijstellingBedrijfsvermogenVanToepassing: 'Nee',
                    basisVrijstellingVanToepassing: 'Ja',
                    vrijstellingBedrijfsvermogen: null as unknown as number

                } as Vrijstellingen;
                schenking.vrijstellingen = vrijstellingen;
            }
            if (!schenking.relatieBegiftigdeMetSchenker?.includes('KIND')) {
                if (vrijstellingen) {
                    vrijstellingen.bedragAlBetaaldDureStudie = undefined;
                    vrijstellingen.bedragNogTeBestedenDureStudie = undefined;
                    vrijstellingen.verhoogdeVrijstellingStudie = false;
                    vrijstellingen.eenmaligVerhoogdeVrijstelling = false;
                    vrijstellingen.vrijstellingSchenkingBelastInIB = false;
                }
            }
            if (schenking.relatieBegiftigdeMetSchenker?.includes('KIND')) {
                const vrijstellingen = schenking.vrijstellingen;
                if (vrijstellingen) {
                    const geboortedatum = sbAangifte.begiftigde?.geboortedatum;
                    const datumSchenking = new Date(sbAangifte.schenking[0].datumSchenking);
                    if (geboortedatum) {
                        const geboorteDatumDate = new Date(geboortedatum);
                        const veertigsteVerjaardag = new Date(geboorteDatumDate);
                        veertigsteVerjaardag.setFullYear(veertigsteVerjaardag.getFullYear() + 40);

                        if (datumSchenking > veertigsteVerjaardag) {
                            vrijstellingen.eenmaligVerhoogdeVrijstelling = false;
                            vrijstellingen.verhoogdeVrijstellingStudie = false;
                        }
                    }
                    if (vrijstellingen.verhoogdeVrijstellingStudie === undefined || !vrijstellingen?.verhoogdeVrijstellingStudie) {
                        vrijstellingen.bedragAlBetaaldDureStudie = undefined;
                        vrijstellingen.bedragNogTeBestedenDureStudie = undefined;
                    }
                }
            }

            if (!schenking.notarieleAkte || Object.keys(schenking.notarieleAkte).length === 0) {
                schenking.notarieleAkte = undefined;
            }
        }

        // bijwerken OG
        if (sbAangifte.schenking && sbAangifte.schenking[0] && sbAangifte.schenking[0].onroerendeZaken != null) {
            if (sbAangifte.schenking[0].onroerendeZaken.adres != null && sbAangifte.schenking[0].onroerendeZaken.adres.landCode === 'NLD') {
                this.hasNLOnroerendeZaken.next(true);
            }
            if (sbAangifte.schenking[0].onroerendeZaken.kwijtgescholdenKoopsom === undefined || sbAangifte.schenking[0].onroerendeZaken.kwijtgescholdenKoopsom === null)
            {
                sbAangifte.schenking[0].onroerendeZaken.kwijtgescholdenKoopsom = 0
            }
        }

        if (sbAangifte.schenking && sbAangifte.schenking[0]) {
            if (!this.hasContanten.value) sbAangifte.schenking[0].contanten = undefined;
            if (!this.hasEffecten.value) sbAangifte.schenking[0].effecten = undefined;
            if (!this.hasOnroerendeZaken.value) {
                sbAangifte.schenking[0].onroerendeZaken = undefined;
                sbAangifte.schenking[0].betaaldeOverdrachtsbelasting = undefined;
                sbAangifte.schenking[0].heffingsgrondslagOverdrachtsbelasting = undefined;
                sbAangifte.schenking[0].datumJuridischeLevering = undefined;
                sbAangifte.schenking[0].indicatieSchenkerVorigeEigenaar = undefined;
            }
            if (!this.hasOnderneming.value) sbAangifte.schenking[0].ondernemingen = undefined;
            if (this.hasOnderneming.value) {
                if (sbAangifte.schenking[0].ondernemingen) {
                    sbAangifte.schenking[0].ondernemingen.indicatieVoldaanAanBezitseisBor = false;
                }
                if (sbAangifte.schenking[0].ondernemingen?.verzoekToepassenBorInSw == true) {
                    sbAangifte.schenking[0].ondernemingen.indicatieVoldaanAanBezitseisBor = true;
                }
                if (sbAangifte.schenking[0].ondernemingen?.verzoekToepassenBorInIb == true) {
                    sbAangifte.schenking[0].ondernemingen.latenteInkomstenBelasting = 0;
                }
            }
            if (!this.hasPU.value) sbAangifte.schenking[0].periodiekeUitkering = undefined;
            if (!this.hasSchuldigerkenning.value) sbAangifte.schenking[0].schuldigerkenning = undefined;
            if (!this.hasKwijtschelding.value) sbAangifte.schenking[0].kwijtscheldingSchuld = undefined;
            if (!this.hasRoerendeZaken.value) sbAangifte.schenking[0].roerendeZaak = undefined;
            if (!this.hasVruchtgebruiken.value) sbAangifte.schenking[0].vruchtgebruik = undefined;
            if (!this.hasOverige.value) sbAangifte.schenking[0].overige = undefined;
        }

        //Hier zetten we waarden in Ontvanger/Schenker/Contactpersoon schoon
        const contactPersoon = sbAangifte.contactPersoon;
        const begiftigde = sbAangifte.begiftigde;
        const schenker = sbAangifte.schenker;
        if (contactPersoon) {
            contactPersoon.geboortedatum = new Date();
            this.emptyStringsToUndefined(contactPersoon);
        }
        if (begiftigde) {
            this.emptyStringsToUndefined(begiftigde);
        }
        if (schenker) {
            this.emptyStringsToUndefined(schenker);
        }
    }

    updateMenuInterface(sbAangifte: AangifteSchenkbelasting) {
        this.updateAangifteSchenkbelasting(sbAangifte);
        const menuInterface: MenuInterface[] = [
            {
                value: 0,
                id: 'Partijen',
                label: 'Partijen',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Schenker',
                        pageMapping: 'Schenker',
                        visible: true,
                        value: 0
                    },
                    {
                        label: 'Ontvanger',
                        pageMapping: 'Ontvanger',
                        visible: true,
                        value: 1
                    },
                    {
                        label: 'Contactpersoon',
                        pageMapping: 'Contactpersoon',
                        visible: true,
                        value: 2
                    }
                ]
            },
            {
                value: 1,
                id: 'Algemeen',
                label: 'Algemeen',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Algemene vragen',
                        pageMapping: 'Algemene vragen',
                        visible: true,
                        value: 10
                    },
                    {
                        label: 'Notariële akte',
                        pageMapping: 'Notariële akte',
                        visible: true,
                        value: 11
                    }
                ]
            },
            {
                value: 2,
                id: 'Schenking',
                label: 'Schenking',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Contanten',
                        pageMapping: 'Contanten',
                        visible: this.hasContanten.value || false,
                        value: 20
                    },
                    {
                        label: 'Effecten',
                        pageMapping: 'Effecten',
                        visible: this.hasEffecten.value || false,
                        value: 21
                    },
                    {
                        label: 'Onroerende zaken',
                        pageMapping: 'Onroerende zaken',
                        visible: this.hasOnroerendeZaken.value || false,
                        value: 22
                    },
                    {
                        label: 'Roerende zaken',
                        pageMapping: 'Roerende zaken',
                        visible: this.hasRoerendeZaken.value || false,
                        value: 23
                    },
                    {
                        label: 'Periodieke uitkeringen',
                        pageMapping: 'Periodieke uitkeringen',
                        visible: this.hasPU.value || false,
                        value: 24
                    },
                    {
                        label: 'Schenking op papier',
                        pageMapping: 'Schenking op papier',
                        visible: this.hasSchuldigerkenning.value || false,
                        value: 25
                    },
                    {
                        label: 'Kwijtscheldingen',
                        pageMapping: 'Kwijtscheldingen',
                        visible: this.hasKwijtschelding.value || false,
                        value: 26
                    },
                    {
                        label: 'Vruchtgebruiken',
                        pageMapping: 'Vruchtgebruiken',
                        visible: this.hasVruchtgebruiken.value || false,
                        value: 27
                    },
                    {
                        label: 'Overige schenkingen',
                        pageMapping: 'Overige schenkingen',
                        visible: this.hasOverige.value || false,
                        value: 28
                    },
                    {
                        label: 'Ondernemingen',
                        pageMapping: 'Ondernemingen',
                        visible: this.hasOnderneming.value || false,
                        value: 29
                    },
                    {
                        label: 'Bedrijfsopvolging',
                        pageMapping: 'Bedrijfsopvolgingsregeling',
                        visible: this.hasOnderneming.value || false,
                        value: 210
                    },
                    {
                        label: 'Selecteer soort bij "Algemene vragen"',
                        pageMapping: '',
                        visible: !this.hasContanten.value && !this.hasOnderneming.value && !this.hasOverige.value && !this.hasVruchtgebruiken.value && !this.hasEffecten.value && !this.hasOnroerendeZaken.value && !this.hasRoerendeZaken.value && !this.hasPU.value && !this.hasSchuldigerkenning.value && !this.hasKwijtschelding.value,
                        value: 210
                    }

                ]
            },
            {
                value: 3,
                id: 'Vrijstellingen',
                label: 'Vrijstellingen',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Vrijstellingen',
                        pageMapping: 'Vrijstellingen',
                        visible: true,
                        value: 30
                    },
                    {
                        label: 'Voorkoming',
                        pageMapping: 'Voorkoming dubbele schenkbelasting',
                        visible: true,
                        value: 31
                    }
                ]
            },
            {
                value: 4,
                id: 'Berekening',
                label: 'Berekening',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Belaste schenking',
                        pageMapping: 'Berekening',
                        visible: true,
                        value: 40
                    }
                ]
            },
            {
                value: 5,
                id: 'Belasting',
                label: 'Belasting',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Schenkbelasting',
                        pageMapping: 'Schenkbelasting',
                        visible: true,
                        value: 50
                    },
                    {
                        label: 'Conserverende aanslag',
                        pageMapping: 'Conserverende aanslag',
                        visible: false,
                        value: 51
                    }
                ]
            }
        ];
        this.menuInterface.next(menuInterface);
    }

    ngOnDestroy(): void {
        if (this.giftTaxEntity) {
            this.giftTaxEntity?.unsubscribe();
        }
        if (this.sbBerekening) {
            this.sbBerekening?.unsubscribe();
        }
        if (this.menuInterface) {
            this.menuInterface?.unsubscribe();
        }
        if (this.errorResponse) {
            this.errorResponse?.unsubscribe();
        }
        if (this.hasContanten) {
            this.hasContanten?.unsubscribe();
        }
        if (this.hasEffecten) {
            this.hasEffecten?.unsubscribe();
        }
        if (this.hasOnroerendeZaken) {
            this.hasOnroerendeZaken?.unsubscribe();
        }
        if (this.hasOnroerendeZaken) {
            this.hasOnroerendeZaken?.unsubscribe();
        }
        if (this.hasOnderneming) {
            this.hasOnderneming?.unsubscribe();
        }
        if (this.hasPU) {
            this.hasPU?.unsubscribe();
        }
        if (this.hasSchuldigerkenning) {
            this.hasSchuldigerkenning?.unsubscribe();
        }
        if (this.hasKwijtschelding) {
            this.hasKwijtschelding?.unsubscribe();
        }
        if (this.hasRoerendeZaken) {
            this.hasRoerendeZaken?.unsubscribe();
        }
        if (this.hasVruchtgebruiken) {
            this.hasVruchtgebruiken?.unsubscribe();
        }
        if (this.hasOverige) {
            this.hasOverige?.unsubscribe();
        }
        if (this.hasNLOnroerendeZaken) {
            this.hasNLOnroerendeZaken?.unsubscribe();
        }
    }

    emptyStringsToUndefined<T extends Record<string, unknown>>(obj: T): void {
        for (const key in obj) {
            if (obj[key] === '') {
                (obj as Record<string, unknown | undefined>)[key] = undefined;
            }
        }
    }
}
